import { Stack } from '@mui/material'
import {
    AllValues,
    assembleAssetTitle,
    CardItem,
    DataSplit,
    getAllValuesCopy,
    IncomeChart,
    isoDateStringToLocalFormat,
    MoneyChart,
    MultiStructuresInfoBox,
    PageGridContainer,
    PageGridItem,
    tokens,
} from 'ComponentLibrary'
import { AssetKeyValuesLoader } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/AssetKeValues.load'
import { useAssetKeyValues } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/AssetKeyValues.data'
import { getDevelopment, getIncome } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/AssetKeyValues.utils'
import { AssetLocation } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/Widgets/AssetLocation'
import { KeyValuesGrid } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/Widgets/KeyValuesGrid'
import { Typography } from 'ComponentLibrary/src/piazza'
import { EnergyEfficiency } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/Widgets/EnergyEfficiency'
import { getUsageTypeSplit, getValuationStructuresWithIndex } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetDetails.utils'
import { NoValuationInfo } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/Widgets/NoValuationInfo'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { isEmpty } from 'lodash'
import { AllValuesExportCard } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/Widgets/AllValuesExportCard'
import { useTranslation } from 'react-i18next'
import { getMultiStructuresInfoBoxCopy } from 'ClientPortal/src/pages/Assets/AssetDetails/multiStructuresInfoBoxCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export const AssetKeyValues = () => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tAllValues } = useTranslation('terminology', { keyPrefix: 'export.allValues' })
    const { t: tTenantUsageTypes } = useTranslation('terminology', { keyPrefix: 'common.tenantUsageTypes' })
    const { t: tAssetKeyValues } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.assetKeyValues' })
    const { t: tAllValuesPage } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.assetKeyValues.allValues' })
    const locale = useCurrentResolvedLocale()
    const { isActualRentInChartsEnabled } = useFlags()
    const {
        asset,
        latestOrder,
        valuations,
        isAssetKeyValuesPending,
        latestFinalizedValuationOrDraft,
        assetEnergySummary,
        isAssetEnergySummaryLoading,
    } = useAssetKeyValues()

    if (isAssetKeyValuesPending) {
        return <AssetKeyValuesLoader />
    }

    if (!asset) {
        return null
    }

    if (!latestFinalizedValuationOrDraft) {
        return (
            <PageGridContainer>
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="267px">
                        <AssetLocation address={asset.address} />
                    </CardItem>
                </PageGridItem>
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="267px">
                        <EnergyEfficiency energySummary={assetEnergySummary} isPending={isAssetEnergySummaryLoading} />
                    </CardItem>
                </PageGridItem>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <NoValuationInfo latestOrder={latestOrder} />
                </PageGridItem>
            </PageGridContainer>
        )
    }

    const development = getDevelopment(valuations, tCommon)
    const income = getIncome(valuations, tAssetKeyValues, tCommon, isActualRentInChartsEnabled)
    const structures = getValuationStructuresWithIndex(asset, latestFinalizedValuationOrDraft)
    const multiStructuresInfoBoxCopy = getMultiStructuresInfoBoxCopy(tCommon)
    const usageTypeSplit = getUsageTypeSplit(latestFinalizedValuationOrDraft.tenant_summary, tTenantUsageTypes)
    const hasIncomeApproach = latestFinalizedValuationOrDraft.selected_valuation_methods.includes('income')
    const isLatestValuationHistorical = latestFinalizedValuationOrDraft.valuation_type === 'historical'
    const isLatestValuationDraft = latestFinalizedValuationOrDraft.is_draft
    const yearFilterUnitLabel = tAssetKeyValues('chart.yearFilterUnitLabel')
    const allValuesCopy = getAllValuesCopy(tCommon, tAllValues, tAllValuesPage)

    return (
        <PageGridContainer>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <Stack direction="row" justifyContent="flex-end">
                    {latestFinalizedValuationOrDraft && (
                        <Typography variant="textS" color={tokens.ColorTextSubtle}>
                            {tAssetKeyValues('basedOn', {
                                source: tAssetKeyValues(
                                    `basedOnSources.${
                                        isLatestValuationDraft
                                            ? isLatestValuationHistorical
                                                ? 'importedDraft'
                                                : 'draft'
                                            : isLatestValuationHistorical
                                              ? 'importedValuation'
                                              : 'valuation'
                                    }`
                                ),
                                date: isoDateStringToLocalFormat(latestFinalizedValuationOrDraft.valuation_date, false, locale),
                            })}
                        </Typography>
                    )}
                </Stack>
            </PageGridItem>
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <CardItem height="267px">
                    <AssetLocation address={asset.address} />
                </CardItem>
            </PageGridItem>
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <CardItem height="267px">
                    <KeyValuesGrid valuation={latestFinalizedValuationOrDraft} />
                </CardItem>
            </PageGridItem>
            {hasIncomeApproach && (
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="267px">
                        <DataSplit
                            title={tCommon('marketRent')}
                            data={usageTypeSplit}
                            valueLabel={tCommon('rent')}
                            percentageLabel={tCommon('share')}
                            size="large"
                            emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                            remainingLegendLabel={tCommon('remaining')}
                            locale={locale}
                        />
                    </CardItem>
                </PageGridItem>
            )}
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <CardItem height="267px">
                    <EnergyEfficiency energySummary={assetEnergySummary} isPending={isAssetEnergySummaryLoading} />
                </CardItem>
            </PageGridItem>
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <CardItem height="510px">
                    <MoneyChart
                        title={tAssetKeyValues('marketValueDevelopmentTitle')}
                        series={development}
                        period="year"
                        currencySymbol="€"
                        showChange={true}
                        yearFilterUnitLabel={yearFilterUnitLabel}
                        trimLeadingZeroes
                        emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                        tooltipTranslations={{
                            draftLabel: tCommon('draft'),
                            yearLabel: tCommon('year'),
                            dateLabel: tCommon('date'),
                        }}
                        locale={locale}
                    />
                </CardItem>
            </PageGridItem>
            {hasIncomeApproach && (
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="510px">
                        <IncomeChart
                            title={tAssetKeyValues('rentAndCostsDevelopmentTitle')}
                            series={income}
                            period="year"
                            currencySymbol="€"
                            showChange={true}
                            yearFilterUnitLabel={yearFilterUnitLabel}
                            trimLeadingZeroes
                            emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                            tooltipTranslations={{
                                draftLabel: tCommon('draft'),
                                yearLabel: tCommon('year'),
                                dateLabel: tCommon('date'),
                            }}
                            locale={locale}
                        />
                    </CardItem>
                </PageGridItem>
            )}
            {!isLatestValuationHistorical && (
                <PageGridItem columnSpan={4} columnSpanXs={4}>
                    <CardItem>
                        <AllValues valuation={latestFinalizedValuationOrDraft} copy={allValuesCopy} locale={locale} />
                    </CardItem>
                </PageGridItem>
            )}
            <PageGridItem columnSpan={2} columnSpanXs={2}>
                <Stack position="sticky" gap={tokens.SizingSpacing300} top={tokens.SizingSpacing200} height="fit-content">
                    <AllValuesExportCard
                        valuation={latestFinalizedValuationOrDraft}
                        addressText={assembleAssetTitle({
                            ...asset?.address,
                            customer_asset_reference: asset?.customer_asset_reference,
                        })}
                    />
                    {!isEmpty(structures) && <MultiStructuresInfoBox structures={structures} copy={multiStructuresInfoBoxCopy} />}
                </Stack>
            </PageGridItem>
        </PageGridContainer>
    )
}
